import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useId,
	useRef,
	useState
} from 'react';

import { useMutation, useMutationState, useQuery } from '@tanstack/react-query';
import {
	CircleCheck,
	ImageIcon,
	Info,
	Loader2,
	Mail,
	MapPin,
	Pen,
	X
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import * as z from 'zod/v4';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { WithStateQuery } from '@/components/ui/with-state';
import { reviseStatuses } from '@/constants/status';
import { useResponse } from '@/hooks/use-dialog';
import { cn } from '@/lib/utils';
import HookFormProvider from '@/providers/hook-form-providers';
import { fileMutations, fileQueries } from '@/queries/file/file.queries';
import { userQueries } from '@/queries/user/user.queries';
import {
	vendorMutations,
	vendorQueries
} from '@/queries/vendor/vendor.queries';
import { noOp } from '@/services/fn';
import { getInitials, onFileRejected } from '@/services/string';
import { useDialogStore } from '@/store';
import { mainSchema } from '@/types/form/account-manager/form';

import FormBank from './form-bank';
import FormDocument from './form-document';
import FormLogin from './form-login';
import FormMain from './form-main';
import FormPic from './form-pic';
import FormSegmentation from './form-segmentation';

export type TabId =
	| 'login'
	| 'main'
	| 'bank'
	| 'segmentation'
	| 'document'
	| 'vao';

type TabActions = {
	isDirty: boolean,
	submit: (() => Promise<void>) | (() => void)
};

type TabFormContextType = {
	registerTab: (tabId: TabId, actions: TabActions) => void,
	getTabActions: (tabId: TabId) => TabActions | undefined
};

const TabFormContext = createContext<TabFormContextType | undefined>(undefined);

export const TabFormProvider = ({
	children
}: {
	children: React.ReactNode
}) => {
	const [tabs, setTabs] = useState<Record<TabId, TabActions>>({
		login: { isDirty: false, submit: noOp },
		bank: { isDirty: false, submit: noOp },
		document: { isDirty: false, submit: noOp },
		main: { isDirty: false, submit: noOp },
		segmentation: { isDirty: false, submit: noOp },
		vao: { isDirty: false, submit: noOp }
	});

	const registerTab = useCallback((tabId: TabId, actions: TabActions) => {
		setTabs((prev) => {
			if (prev[tabId]?.isDirty === actions.isDirty) {
				return prev;
			}
			return { ...prev, [tabId]: actions };
		});
	}, []);

	const getTabActions = useCallback((tabId: TabId) => tabs[tabId], [tabs]);

	return (
		<TabFormContext.Provider value={{ registerTab, getTabActions }}>
			{children}
		</TabFormContext.Provider>
	);
};

export const useTabManager = () => {
	const context = useContext(TabFormContext);
	if (!context)
		throw new Error('useTabManager must be used within TabFormProvider');
	return context;
};

export const loginSchema = z.object({ name: z.string(), email: z.email() });

function DataLogin() {
	const user = useQuery(userQueries.user());

	return (
		<WithStateQuery state={user}>
			{({ data: { name, email } }) => (
				<HookFormProvider
					schema={loginSchema}
					onSubmit={() => {}}
					defaultValues={{
						name,
						email
					}}
					className="my-2"
				>
					<FormLogin />
				</HookFormProvider>
			)}
		</WithStateQuery>
	);
}

function DataMain({
	vendorName,
	isDisabled
}: Readonly<{ vendorName: string, isDisabled?: boolean }>) {
	const formId = useId();

	const user = useQuery(userQueries.user());
	const main = useQuery(vendorQueries.mainData());

	return (
		<WithStateQuery state={[user, main]}>
			{([
				{
					data: { name }
				},
				{
					data: {
						address,
						company_id,
						business_area_id,
						qualification_id,
						type_cooperations,
						website,
						phone_number,
						province_id,
						city_id,
						district_id,
						village_id,
						npwp,
						postal_code,
						npwp_name,
						npwp_address,
						npwp_file_name,
						pic_email,
						pic_name,
						pic_nik,
						pic_phone_number
					}
				}
			]) => (
				<HookFormProvider
					schema={mainSchema}
					defaultValues={{
						name,
						company_id,
						business_area_id,
						qualification_id,
						type_cooperations: type_cooperations?.map((v) => v.id)?.filter(Boolean),
						website,
						phone_number,
						address,
						province_id,
						city_id,
						district_id,
						village_id,
						postal_code,
						npwp,
						npwp_name,
						npwp_address,
						npwp_file_name,
						pic_email,
						pic_name,
						pic_nik,
						pic_phone_number
					}}
					onSubmit={() => {}}
					className="my-2"
					formOptions={{
						disabled: isDisabled,
						mode: 'onChange',
						reValidateMode: 'onChange'
					}}
					id={formId}
				>
					<FormMain vendorName={vendorName} />
				</HookFormProvider>
			)}
		</WithStateQuery>
	);
}

function DataBank(
	props: Readonly<{ vendorId: number, vendorName: string, isDisabled?: boolean }>
) {
	return <FormBank {...props} />;
}

function DataSegmentation(
	props: Readonly<{ vendorId: number, isDisabled?: boolean }>
) {
	return <FormSegmentation {...props} />;
}

function DataDocument(
	props: Readonly<{ vendorId: number, vendorName: string, isDisabled?: boolean }>
) {
	return <FormDocument {...props} />;
}

function DataPic(
	props: Readonly<{ vendorId: number, vendorName: string, isDisabled?: boolean }>
) {
	return <FormPic {...props} />;
}

function ProgressBar() {
	const section = useQuery(vendorQueries.sectionStatus());

	return (
		<div className="relative h-5 w-64 overflow-hidden rounded-sm bg-gray-200">
			<div
				className="absolute inset-0 opacity-50"
				style={{
					backgroundImage:
						'linear-gradient(-45deg, #ffffff 25%, transparent 25%, transparent 50%, #ffffff 50%, #ffffff 75%, transparent 75%, transparent)',
					backgroundSize: '.5rem .5rem'
				}}
			/>
			<WithStateQuery state={section}>
				{({
					data: {
						hasBankData,
						hasMainData,
						hasPersonResponsibleData,
						hasSegmentationData,
						hasDocumentData
					}
				}) => (
					<div
						className="relative flex h-full items-center justify-end rounded-l-sm bg-emerald-500"
						style={{
							width: `${(+hasMainData + +hasBankData + +hasPersonResponsibleData + +hasSegmentationData + +hasDocumentData) * 20}%`
						}}
					>
						<div className="mr-0.5 rounded-sm bg-emerald-400 px-2 py-0.5 text-xs leading-none font-bold text-white shadow-sm">
							{(+hasMainData +
								+hasBankData +
								+hasPersonResponsibleData +
								+hasSegmentationData +
								+hasDocumentData) *
								20}
							%
						</div>
					</div>
				)}
			</WithStateQuery>
		</div>
	);
}

function AvatarProfile({ fileKey }: Readonly<{ fileKey: string }>) {
	const file = useQuery(fileQueries.getPresign({ key: fileKey }));

	return (
		<WithStateQuery state={file}>
			{({ data: { url } }) => <AvatarImage src={url} className="h-full w-full" />}
		</WithStateQuery>
	);
}

function PreviewFile({
	fileKey,
	isDirectUrl = false,
	path
}: Readonly<{
	fileKey: string,
	isDirectUrl?: boolean,
	path: 'profile-image' | 'cover-image'
}>) {
	const file = useQuery({
		...fileQueries.getPresign({ key: fileKey }),
		enabled: !isDirectUrl
	});

	if (isDirectUrl) {
		return (
			<img
				src={fileKey}
				alt={path}
				className={cn('rounded-lg object-contain shadow-sm', {
					'size-24 rounded-full': path === 'profile-image',
					'aspect-video h-32 w-full object-fill': path === 'cover-image'
				})}
			/>
		);
	}
	return (
		<WithStateQuery state={file}>
			{({ data: { url } }) => (
				<img
					src={url}
					alt={path}
					className={cn('rounded-lg object-contain shadow-sm', {
						'size-24 rounded-full': path === 'profile-image',
						'aspect-video': path === 'cover-image'
					})}
				/>
			)}
		</WithStateQuery>
	);
}

function UpdateProfileVendor({
	photo,
	vendorName,
	path,
	onSuccess: onSuccessUpdate
}: Readonly<{
	photo: string | null,
	vendorName: string,
	path: 'profile-image' | 'cover-image',
	onSuccess: () => void
}>) {
	const setDialog = useDialogStore((s) => s.setStore);
	const setAction = useDialogStore((s) => s.setAction);
	const onOpen = useDialogStore((s) => s.onOpenChange);

	const [progress, setProgress] = useState<number>(0);
	const [previewUrl, setPreviewUrl] = useState<{
		key: string | null,
		isDirectUrl: boolean
	}>({ key: photo, isDirectUrl: false });
	const [fileKey, setFileKey] = useState<string | null>(photo);

	const uploadMutation = useMutation(fileMutations.uploadPresign);
	const submit = useMutation(vendorMutations.photo);
	const { onError } = useResponse();

	const { getRootProps, getInputProps, open, isDragAccept } = useDropzone({
		accept: {
			'image/png': ['.png'],
			'image/jpeg': ['.jpg', '.jpeg']
		},
		disabled: uploadMutation.isPending,
		noKeyboard: true,
		onDropAccepted: (acceptedFiles) => {
			const file = acceptedFiles[0];
			if (!file) return;

			setPreviewUrl({ key: null, isDirectUrl: false });
			setProgress(0);

			const fileKey = `${vendorName}/${crypto.randomUUID()}/${path}.${file.name.split('.').pop()}`;

			uploadMutation.mutate(
				{
					key: fileKey,
					file: file,
					onProgress: (val) => setProgress(val)
				},
				{
					onSuccess: (sasUrl) => {
						setFileKey(fileKey);
						setPreviewUrl({ key: sasUrl, isDirectUrl: true });
					},
					onError: noOp
				}
			);
		},
		noClick: true,
		multiple: false,
		maxSize: 5_242_880,
		onDropRejected: (err) => {
			const errFile = err[0];
			if (!errFile) return;

			onError({
				message: onFileRejected({ code: errFile.errors[0]?.code, type: 'image' }),
				confirmText: 'Coba Lagi'
			});
		}
	});

	const handleReset = () => {
		setFileKey(null);
		setPreviewUrl({ key: null, isDirectUrl: false });
		setProgress(0);
		uploadMutation.reset();
	};

	function onSuccess({ message }: { message: string }) {
		setDialog('variant', 'success');
		setDialog('title', 'Berhasil');
		setDialog('description', message);
		setDialog('confirmText', 'OK');
		setAction('onConfirm', () => {
			onOpen(false);
			onSuccessUpdate();
		});
		setDialog('open', true);
	}

	return (
		<DialogContent>
			<DialogTitle className="flex flex-col">
				<span className="text-lg text-black">
					Unggah {path === 'profile-image' ? 'Foto' : 'Banner'} Profil
				</span>
				<span className="text-label-input">
					Silakan unggah {path === 'profile-image' ? 'Foto' : 'Banner'} Profil Anda
				</span>
			</DialogTitle>
			<DialogDescription />
			<div
				{...getRootProps({
					className: cn(
						'dropzone border-2 border-slate-300 border-dashed h-36 flex flex-col gap-1 justify-center items-center p-8 text-center rounded-lg transition delay-100 duration-300 ease-in-out',
						isDragAccept && 'bg-slate-400/50'
					)
				})}
			>
				<input {...getInputProps()} />

				{uploadMutation.isPending ? (
					<div className="z-10 flex flex-col items-center gap-2">
						<Loader2 className="size-8 animate-spin text-blue-500" />
						<span className="text-sm font-medium">Uploading... {progress}%</span>
					</div>
				) : null}

				{!uploadMutation.isPending && previewUrl.key ? (
					<div className="relative flex h-full w-full items-center justify-center p-2">
						<PreviewFile
							fileKey={previewUrl.key}
							isDirectUrl={previewUrl.isDirectUrl}
							path={path}
						/>
						<button
							onClick={(e) => {
								e.stopPropagation();
								handleReset();
							}}
							className="absolute -top-2 -right-2 rounded-full border bg-white p-1 shadow-md transition hover:bg-gray-100"
						>
							<X className="size-4 text-gray-500" />
						</button>
					</div>
				) : (
					/* 3. IDLE STATE */
					!uploadMutation.isPending && (
						<>
							<div
								className="flex w-full cursor-pointer items-center justify-center rounded bg-neutral-50 p-4 transition hover:bg-neutral-100"
								onClick={open}
								role="none"
							>
								<ImageIcon className="size-6 text-gray-400" />
							</div>
							<span className="mt-4 font-medium text-black">
								Unggah {path === 'profile-image' ? 'Foto' : 'Banner'} Profil
							</span>
							<span className="text-secondary mt-1 text-xs">
								Unggah file JPG, JPEG, PNG (maks. 5MB)
							</span>
						</>
					)
				)}
			</div>
			<div className="flex justify-end">
				<Button
					type="button"
					disabled={photo === fileKey}
					onClick={() => {
						submit.mutate(
							{ path, file_name: fileKey },
							{
								onSuccess: () =>
									onSuccess({
										message: `${path === 'profile-image' ? 'Foto' : 'Banner'} Profil Berhasil Diubah`
									}),
								onError: ({ message }) => onError({ message })
							}
						);
					}}
				>
					Simpan
				</Button>
			</div>
		</DialogContent>
	);
}

function PhotoProfileVendor({
	vendorName
}: Readonly<{ vendorName: string, vendorId: number }>) {
	const main = useQuery(vendorQueries.mainData());
	const section = useQuery(vendorQueries.sectionStatus());

	const [visible, setVisible] = useState<boolean>(false);

	return (
		<>
			<div className="relative size-24">
				<Avatar className="size-24 rounded-full border border-black/10">
					<WithStateQuery state={main}>
						{({ data: { photo } }) =>
							photo ? <AvatarProfile fileKey={photo} /> : null
						}
					</WithStateQuery>
					<AvatarFallback className="rounded-full border border-gray-400 text-3xl">
						{getInitials(vendorName ?? '')}
					</AvatarFallback>
				</Avatar>
				<WithStateQuery state={section}>
					{({ data: { canSendRequest } }) =>
						canSendRequest ? (
							<div className="absolute right-0 bottom-0 size-7">
								<Button
									size={'icon'}
									variant={'ghost'}
									className="size-7 rounded-full bg-gray-300 hover:bg-gray-200"
									onClick={() => setVisible(true)}
								>
									<Pen size={12} color="#fff" />
								</Button>
							</div>
						) : null
					}
				</WithStateQuery>
			</div>
			<Dialog open={visible} onOpenChange={setVisible}>
				{visible ? (
					<WithStateQuery state={main}>
						{({ data: { photo } }) => (
							<UpdateProfileVendor
								photo={photo}
								vendorName={vendorName}
								path="profile-image"
								onSuccess={() => setVisible(false)}
							/>
						)}
					</WithStateQuery>
				) : null}
			</Dialog>
		</>
	);
}

function CoverImage({ fileKey }: Readonly<{ fileKey: string }>) {
	const file = useQuery(fileQueries.getPresign({ key: fileKey }));
	return (
		<WithStateQuery state={file}>
			{({ data: { url } }) => (
				<img
					src={url}
					alt="cover-image"
					className="aspect-video h-32 w-full overflow-hidden bg-auto"
				/>
			)}
		</WithStateQuery>
	);
}

function PhotoCoverVendor({ vendorName }: Readonly<{ vendorName: string }>) {
	const main = useQuery(vendorQueries.mainData());
	const section = useQuery(vendorQueries.sectionStatus());

	const [visible, setVisible] = useState<boolean>(false);

	return (
		<div className="relative h-full w-full">
			<WithStateQuery state={main}>
				{({ data: { cover } }) => (cover ? <CoverImage fileKey={cover} /> : null)}
			</WithStateQuery>

			<div className="pointer-events-none absolute inset-0 bg-linear-to-b from-black/60 via-transparent to-transparent" />

			<WithStateQuery state={section}>
				{({ data: { canSendRequest } }) =>
					canSendRequest ? (
						<div className="absolute top-2 right-2">
							<Button
								size={'sm'}
								variant={'ghost'}
								className="border border-white/50 text-white hover:bg-white/20 hover:text-white"
								onClick={() => setVisible(true)}
							>
								Ubah Banner
								<Pen size={12} className="text-white" />
							</Button>
						</div>
					) : null
				}
			</WithStateQuery>
			<Dialog open={visible} onOpenChange={setVisible}>
				{visible ? (
					<WithStateQuery state={main}>
						{({ data: { cover } }) => (
							<UpdateProfileVendor
								photo={cover}
								vendorName={vendorName}
								path="cover-image"
								onSuccess={() => setVisible(false)}
							/>
						)}
					</WithStateQuery>
				) : null}
			</Dialog>
		</div>
	);
}

export default function AmContent() {
	const user = useQuery(userQueries.user());
	const section = useQuery(vendorQueries.sectionStatus());
	const document = useQuery(vendorQueries.documentData());
	const main = useQuery(vendorQueries.mainData());
	const mainDataMutationStates = useMutationState({
		filters: { mutationKey: ['mainData'] },
		select: ({ state }) => state
	});
	const submitRequest = useMutation(vendorMutations.submitRequest);

	const [tab, setTab] = useState<TabId>('login');

	const onOpen = useDialogStore((s) => s.onOpenChange);
	const setDialog = useDialogStore((s) => s.setStore);
	const setAction = useDialogStore((s) => s.setAction);
	const { onError, onSuccess } = useResponse();

	const { getTabActions, registerTab } = useTabManager();
	const tabRef = useRef<{
		pending: TabId | null,
		onSuccess: () => void,
		type: 'onChange' | 'draft'
	} | null>(null);

	const handleTabChange = (newTab: string) => {
		const currentTabActions = getTabActions(tab);

		if (currentTabActions?.isDirty) {
			tabRef.current = {
				pending: newTab as TabId,
				onSuccess: () => {
					setTab(newTab as TabId);
					tabRef.current = null;
					onOpen(false);
				},
				type: 'onChange'
			};
			setDialog('variant', 'warning');
			setDialog('title', 'Simpan Data?');
			setDialog(
				'description',
				'Anda memiliki data yang belum disimpan. Simpan sebelum berpindah halaman?'
			);
			setDialog('confirmText', 'Ya, Simpan');
			onOpen(true);
			setAction('onConfirm', handleConfirmSave);
		} else {
			setTab(newTab as TabId);
		}
	};

	function handleConfirmSave() {
		const currentTabActions = getTabActions(tab);
		if (currentTabActions?.submit && currentTabActions?.isDirty) {
			currentTabActions.submit();

			return;
		}
		onOpen(false);
	}

	function handleSaveAsDraft() {
		const currentTabActions = getTabActions(tab);
		if (currentTabActions?.submit && tab === 'main') {
			setDialog('variant', 'warning');
			setDialog('title', 'Simpan Sebagai Draft?');
			setDialog(
				'description',
				'Apakah Anda yakin ingin menyimpan progres pengisian?'
			);
			setDialog('cancelText', 'Tutup');
			setDialog('confirmText', 'Ya, Simpan');
			onOpen(true);

			if (currentTabActions.isDirty) {
				tabRef.current = {
					pending: null,
					onSuccess: () => {
						setDialog('variant', 'success');
						setDialog('title', 'Draft Berhasil Disimpan');
						setDialog(
							'description',
							'Perubahan Anda telah disimpan sebagai draft. Anda dapat melanjutkan pengisian kapan saja sebelum mengirim dokumen.'
						);
						setDialog('confirmText', 'Tutup');
						setDialog('cancelText', null);
						setAction('onConfirm', () => {
							onOpen(false);
							tabRef.current = null;
						});

						registerTab('main', { isDirty: false, submit: noOp });
					},
					type: 'draft'
				};
				setAction('onConfirm', () => currentTabActions.submit());
			} else {
				tabRef.current = {
					pending: null,
					onSuccess: () => {
						setDialog('variant', 'success');
						setDialog('title', 'Draft Berhasil Disimpan');
						setDialog(
							'description',
							'Perubahan Anda telah disimpan sebagai draft. Anda dapat melanjutkan pengisian kapan saja sebelum mengirim dokumen.'
						);
						setDialog('confirmText', 'Tutup');
						setDialog('cancelText', null);
						setAction('onConfirm', () => {
							onOpen(false);
							tabRef.current = null;
						});
					},
					type: 'draft'
				};

				setAction('onConfirm', () => {
					setDialog('variant', 'success');
					setDialog('title', 'Draft Berhasil Disimpan');
					setDialog(
						'description',
						'Perubahan Anda telah disimpan sebagai draft. Anda dapat melanjutkan pengisian kapan saja sebelum mengirim dokumen.'
					);
					setDialog('confirmText', 'Tutup');
					setDialog('cancelText', null);

					setAction('onConfirm', () => {
						onOpen(false);
						tabRef.current = null;
					});
				});
			}

			return;
		}

		setDialog('variant', 'success');
		setDialog('title', 'Draft Berhasil Disimpan');
		setDialog(
			'description',
			'Perubahan Anda telah disimpan sebagai draft. Anda dapat melanjutkan pengisian kapan saja sebelum mengirim dokumen.'
		);
		setDialog('confirmText', 'Tutup');
		setDialog('cancelText', null);
		onOpen(true);
		setAction('onConfirm', () => {
			onOpen(false);
			tabRef.current = null;
		});
	}

	useEffect(() => {
		const mainDataMutationState = mainDataMutationStates.at(-1);
		if (tabRef.current && mainDataMutationState?.status === 'success') {
			tabRef.current.onSuccess();
		}
	}, [mainDataMutationStates]);

	function handleSubmit() {
		setDialog('variant', 'warning');
		setDialog('title', 'Kirim Data Profil');
		setDialog(
			'description',
			<span className="flex flex-col items-center justify-center gap-4">
				<span>Seluruh data yang dikirim akan melalui proses verifikasi.</span>
				<span>
					Mohon pastikan kebenaran dan kelengkapan informasi sebelum mengirimkan
					dokumen
				</span>
			</span>
		);
		setDialog('cancelText', 'Batal');
		setDialog('confirmText', 'Ya, Kirim');
		setAction('onConfirm', () =>
			submitRequest.mutate(undefined, {
				onSuccess: () =>
					onSuccess({
						message:
							'Seluruh dokumen Anda telah berhasil dikirim. Silakan menunggu proses verifikasi oleh PAM'
					}),
				onError: ({ message }) => onError({ message })
			})
		);
		setAction('onCancel', () => onOpen(true));
		onOpen(true);
	}

	return (
		<>
			<Card className="py-0">
				<CardHeader className="relative p-5">
					<div className="to-primary/70 relative h-32 w-full rounded-md bg-linear-to-r from-gray-300">
						<WithStateQuery state={user}>
							{({ data: { vendorName } }) =>
								vendorName ? <PhotoCoverVendor vendorName={vendorName} /> : null
							}
						</WithStateQuery>
					</div>
					<WithStateQuery state={user}>
						{({ data: { vendorName, vendorEmail, vendorId } }) => (
							<div className="-mt-10 flex items-end gap-3">
								{vendorName && vendorId ? (
									<PhotoProfileVendor vendorName={vendorName} vendorId={vendorId} />
								) : null}
								<div className="flex h-full flex-col text-black">
									<p className="text-2xl">{vendorName}</p>
									<WithStateQuery state={main}>
										{({ data: { phone_number, city_name, province_name } }) => (
											<div className="text-secondary flex items-center justify-center gap-2">
												<Mail size={16} />
												<p className="text-sm">{vendorEmail}</p>
												<MapPin size={16} />
												<p className="text-sm">
													{[province_name, city_name].filter(Boolean).join(' - ')}
												</p>
												<img
													src="/icon/whatsapp.webp"
													alt="icon-upload"
													className="size-4"
												/>
												<p className="text-sm">{phone_number}</p>
											</div>
										)}
									</WithStateQuery>
								</div>
							</div>
						)}
					</WithStateQuery>
				</CardHeader>
				<CardContent className="px-5">
					<Tabs defaultValue="login" value={tab} onValueChange={handleTabChange}>
						<div className="bg-primary-foreground flex overflow-x-auto border-b border-b-neutral-200">
							<TabsList className="border-secondary/40 rounded-none border-b-0 bg-transparent px-0 pb-0">
								<TabsTrigger
									value="login"
									className="data-[state=active]:border-b-primary text-secondary flex h-full items-center justify-center gap-2 rounded-none border-b-0 py-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-black data-[state=active]:shadow-none"
									onClick={() => setTab('login')}
								>
									<span>Data Login</span>
									<CircleCheck className="text-green-500" />
								</TabsTrigger>
								<TabsTrigger
									value="main"
									className="data-[state=active]:border-b-primary text-secondary flex h-full items-center justify-center gap-2 rounded-none border-b-0 py-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-black data-[state=active]:shadow-none"
									onClick={() => setTab('main')}
								>
									<span>Data Utama</span>
									<WithStateQuery state={section}>
										{({ data: { hasMainData } }) => (
											<CircleCheck
												className={cn('text-green-500', {
													'text-gray-300': !hasMainData
												})}
											/>
										)}
									</WithStateQuery>
								</TabsTrigger>
								<TabsTrigger
									value="bank"
									className="data-[state=active]:border-b-primary text-secondary flex h-full items-center justify-center gap-2 rounded-none border-b-0 py-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-black data-[state=active]:shadow-none"
									onClick={() => setTab('bank')}
								>
									<span>Data Bank</span>
									<WithStateQuery state={section}>
										{({ data: { hasBankData } }) => (
											<CircleCheck
												className={cn('text-green-500', {
													'text-gray-300': !hasBankData
												})}
											/>
										)}
									</WithStateQuery>
								</TabsTrigger>
								<TabsTrigger
									value="segmentation"
									className="data-[state=active]:border-b-primary text-secondary flex h-full items-center justify-center gap-2 rounded-none border-b-0 py-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-black data-[state=active]:shadow-none"
									onClick={() => setTab('segmentation')}
								>
									<span>Data Segmentasi</span>
									<WithStateQuery state={section}>
										{({ data: { hasSegmentationData } }) => (
											<CircleCheck
												className={cn('text-green-500', {
													'text-gray-300': !hasSegmentationData
												})}
											/>
										)}
									</WithStateQuery>
								</TabsTrigger>
								<TabsTrigger
									value="document"
									className="data-[state=active]:border-b-primary text-secondary flex h-full items-center justify-center gap-2 rounded-none border-b-0 py-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-black data-[state=active]:shadow-none"
									onClick={() => setTab('document')}
								>
									<span>Data Dokumen</span>
									<WithStateQuery state={[section, document]}>
										{([
											{
												data: { hasDocumentData }
											},
											{ data: document }
										]) =>
											document.some((doc) => reviseStatuses.has(doc.statusId)) ? (
												<Info className={'text-yellow-400'} />
											) : (
												<CircleCheck
													className={cn('text-green-500', {
														'text-gray-300': !hasDocumentData
													})}
												/>
											)
										}
									</WithStateQuery>
								</TabsTrigger>
								<TabsTrigger
									value="vao"
									className="data-[state=active]:border-b-primary text-secondary flex h-full items-center justify-center gap-2 rounded-none border-b-0 py-2 data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:font-bold data-[state=active]:text-black data-[state=active]:shadow-none"
									onClick={() => setTab('vao')}
								>
									<span>Data Penanggung Jawab</span>
									<WithStateQuery state={section}>
										{({ data: { hasPersonResponsibleData } }) => (
											<CircleCheck
												className={cn('text-green-500', {
													'text-gray-300': !hasPersonResponsibleData
												})}
											/>
										)}
									</WithStateQuery>
								</TabsTrigger>
							</TabsList>
						</div>
						<TabsContent value="login">
							<DataLogin />
						</TabsContent>
						<TabsContent value="main">
							<WithStateQuery state={[user, section]}>
								{([
									{
										data: { vendorId, vendorName }
									},
									{
										data: { canSendRequest }
									}
								]) =>
									vendorId && vendorName ? (
										<DataMain vendorName={vendorName} isDisabled={!canSendRequest} />
									) : null
								}
							</WithStateQuery>
						</TabsContent>
						<TabsContent value="bank">
							<WithStateQuery state={[user, section]}>
								{([
									{
										data: { vendorId, vendorName }
									},
									{
										data: { canSendRequest }
									}
								]) =>
									vendorId && vendorName ? (
										<DataBank
											vendorId={vendorId}
											vendorName={vendorName}
											isDisabled={!canSendRequest}
										/>
									) : null
								}
							</WithStateQuery>
						</TabsContent>
						<TabsContent value="segmentation">
							<WithStateQuery state={[user, section]}>
								{([
									{
										data: { vendorId }
									},
									{
										data: { canSendRequest }
									}
								]) =>
									vendorId ? (
										<DataSegmentation vendorId={vendorId} isDisabled={!canSendRequest} />
									) : null
								}
							</WithStateQuery>
						</TabsContent>
						<TabsContent value="document">
							<WithStateQuery state={[user, section]}>
								{([
									{
										data: { vendorId, vendorName }
									},
									{
										data: { canSendRequest, canResubmitRequest }
									}
								]) =>
									vendorId && vendorName ? (
										<DataDocument
											vendorId={vendorId}
											vendorName={vendorName}
											isDisabled={!canSendRequest && !canResubmitRequest}
										/>
									) : null
								}
							</WithStateQuery>
						</TabsContent>
						<TabsContent value="vao">
							<WithStateQuery state={[user, section]}>
								{([
									{
										data: { vendorId, vendorName }
									},
									{
										data: { canSendRequest }
									}
								]) =>
									vendorId && vendorName ? (
										<DataPic
											vendorId={vendorId}
											vendorName={vendorName}
											isDisabled={!canSendRequest}
										/>
									) : null
								}
							</WithStateQuery>
						</TabsContent>
					</Tabs>
				</CardContent>
			</Card>
			<Card className="p-0">
				<CardContent className="flex flex-row items-center justify-between px-3">
					<WithStateQuery state={section}>
						{({ data: { canSendRequest } }) =>
							canSendRequest ? (
								<Button
									variant={'ghost'}
									className="text-primary hover:text-primary/70"
									onClick={handleSaveAsDraft}
								>
									Simpan Sebagai Draft
								</Button>
							) : (
								<div />
							)
						}
					</WithStateQuery>
					<div className="flex items-stretch justify-center gap-3">
						<div className="flex flex-col items-end gap-1">
							<ProgressBar />

							<span className="text-secondary text-xs">
								Jumlah data profil terisi{' '}
								<WithStateQuery state={section} loading={'1/6'}>
									{({
										data: {
											hasBankData,
											hasDocumentData,
											hasMainData,
											hasPersonResponsibleData,
											hasSegmentationData
										}
									}) => (
										<>
											{[
												hasBankData,
												hasDocumentData,
												hasMainData,
												hasPersonResponsibleData,
												hasSegmentationData
											].filter(Boolean).length + 1}
											/6
										</>
									)}
								</WithStateQuery>
							</span>
						</div>
						<WithStateQuery state={section}>
							{({
								data: {
									hasBankData,
									hasDocumentData,
									hasMainData,
									hasPersonResponsibleData,
									hasSegmentationData,
									canSendRequest,
									canResubmitRequest
								}
							}) =>
								canSendRequest || canResubmitRequest ? (
									<Button
										className="h-auto px-8"
										disabled={
											![
												hasBankData,
												hasDocumentData,
												hasMainData,
												hasPersonResponsibleData,
												hasSegmentationData
											].every(Boolean)
										}
										type="button"
										onClick={handleSubmit}
									>
										Kirim
									</Button>
								) : null
							}
						</WithStateQuery>
					</div>
				</CardContent>
			</Card>
		</>
	);
}
